/**
 * Leverage calculation utilities for Euler borrow pairs.
 * These formulas match the Euler protocol's leverage calculations.
 */

/**
 * Calculate max multiplier from borrow LTV using Euler's formula:
 * result = (10000 * LTV) / (10000 - LTV) - 200 + 10000
 * maxMultiplier = result / 10000
 *
 * This matches the selectMaxLeverageForTwoDifferentAssets selector.
 *
 * @param borrowLTV - The borrow LTV in basis points (e.g., 9000 = 90%)
 * @returns The max multiplier (e.g., 14.27x) or undefined if invalid
 */
export function calculateMaxMultiplier(borrowLTV: bigint | undefined): number | undefined {
  if (borrowLTV === undefined || borrowLTV === 0n) return undefined
  const ltvBps = Number(borrowLTV)
  if (ltvBps >= 10000) return undefined // Invalid LTV >= 100%
  const result = (10000 * ltvBps) / (10000 - ltvBps) - 200 + 10000
  return result / 10000
}

/**
 * Calculate max ROE (Return on Equity) using Euler's formula:
 * netApy = supplyAPY - borrowAPY
 * maxROE = supplyAPY + (maxMultiplier - 1) * netApy
 *
 * @param supplyAPY - The supply APY as a percentage (e.g., 5.5 for 5.5%)
 * @param borrowAPY - The borrow APY as a percentage (e.g., 2.86 for 2.86%)
 * @param maxMultiplier - The max multiplier from calculateMaxMultiplier
 * @returns The max ROE as a percentage or undefined if inputs are invalid
 */
export function calculateMaxRoe(
  supplyAPY: number | undefined,
  borrowAPY: number | undefined,
  maxMultiplier: number | undefined,
): number | undefined {
  if (supplyAPY === undefined || borrowAPY === undefined || maxMultiplier === undefined) return undefined
  const netAPY = supplyAPY - borrowAPY
  return supplyAPY + (maxMultiplier - 1) * netAPY
}
